import React, { useState } from 'react';
import { OnboardingStatusResponse } from '../types';

interface OnboardingToSProps {
  token: string;
  onboardingData: OnboardingStatusResponse;
  onStepComplete: (step: string, data?: Record<string, unknown>) => Promise<void>;
  onAccept: () => Promise<void>;
  error: string | null;
}

/**
 * Terms of Service acceptance step for Fair Manager onboarding
 * Issue #1455 - Step 2 of 7: Required step before proceeding
 */
const OnboardingToS: React.FC<OnboardingToSProps> = ({ onAccept }) => {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!accepted) {
      setError('Please read and accept the Terms of Service to continue.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onAccept();
    } catch (err) {
      setError('Failed to accept Terms of Service. Please try again.');
      console.error('Failed to accept ToS:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Terms of Service</h2>
        <p className="text-gray-600">
          Please review and accept our Terms of Service to continue with the onboarding process.
        </p>
      </div>

      {/* Terms of Service content */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-6 mb-6 max-h-96 overflow-y-auto">
        <div className="prose prose-sm max-w-none text-gray-700">
          <h3 className="text-lg font-semibold mb-3">Fair Manager Agreement</h3>

          <h4 className="font-semibold mt-4 mb-2">1. Account Responsibilities</h4>
          <p className="mb-3">
            As a Fair Manager on Local Artist Fairs, you agree to:
          </p>
          <ul className="list-disc pl-6 mb-3 space-y-1">
            <li>Maintain accurate and up-to-date fair information</li>
            <li>Respond to vendor inquiries in a timely manner</li>
            <li>Honor all confirmed booth reservations</li>
            <li>Comply with all applicable local, state, and federal laws</li>
          </ul>

          <h4 className="font-semibold mt-4 mb-2">2. Fair Management</h4>
          <p className="mb-3">
            You are responsible for:
          </p>
          <ul className="list-disc pl-6 mb-3 space-y-1">
            <li>Setting fair dates, locations, and booth configurations</li>
            <li>Reviewing and approving/rejecting vendor booth reservation requests</li>
            <li>Managing fair capacity and waitlists</li>
            <li>Communicating fair updates to registered vendors</li>
          </ul>

          <h4 className="font-semibold mt-4 mb-2">3. Payments and Fees</h4>
          <p className="mb-3">
            Platform fees and payment processing:
          </p>
          <ul className="list-disc pl-6 mb-3 space-y-1">
            <li>Platform service fees are clearly disclosed before transactions</li>
            <li>Payments are processed securely through Stripe</li>
            <li>You are responsible for any taxes on revenues collected</li>
            <li>Refunds are handled according to your fair's refund policy</li>
          </ul>

          <h4 className="font-semibold mt-4 mb-2">4. Content and Conduct</h4>
          <p className="mb-3">
            You agree to:
          </p>
          <ul className="list-disc pl-6 mb-3 space-y-1">
            <li>Provide accurate fair descriptions and details</li>
            <li>Not post misleading or fraudulent information</li>
            <li>Treat all vendors fairly and without discrimination</li>
            <li>Not engage in any activities that harm the platform or its users</li>
          </ul>

          <h4 className="font-semibold mt-4 mb-2">5. Liability and Indemnification</h4>
          <p className="mb-3">
            Local Artist Fairs is a platform connecting fair managers and vendors. You agree that:
          </p>
          <ul className="list-disc pl-6 mb-3 space-y-1">
            <li>You are solely responsible for the operation and management of your fair</li>
            <li>The platform is not liable for any disputes between you and vendors</li>
            <li>You will indemnify the platform against claims arising from your fair</li>
          </ul>

          <h4 className="font-semibold mt-4 mb-2">6. Termination</h4>
          <p className="mb-3">
            We reserve the right to suspend or terminate your account if you violate these terms
            or engage in fraudulent activities. You may close your account at any time by
            contacting support.
          </p>

          <h4 className="font-semibold mt-4 mb-2">7. Changes to Terms</h4>
          <p className="mb-3">
            We may update these terms from time to time. Continued use of the platform after
            changes constitutes acceptance of the updated terms.
          </p>

          <p className="mt-6 text-sm text-gray-600">
            Last updated: January 2026
          </p>
        </div>
      </div>

      {/* Acceptance checkbox */}
      <div className="mb-6">
        <label className="flex items-start cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => {
              setAccepted(e.target.checked);
              setError(null);
            }}
            className="mt-1 h-5 w-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            aria-describedby="tos-description"
          />
          <span className="ml-3 text-gray-700" id="tos-description">
            I have read and agree to the Terms of Service
          </span>
        </label>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4" role="alert">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleAccept}
          disabled={loading}
          className={`
            px-6 py-2 rounded-md font-medium transition-colors
            ${
              loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
            }
          `}
        >
          {loading ? 'Accepting...' : 'Accept and Continue'}
        </button>
      </div>
    </div>
  );
};

export default OnboardingToS;
